/**
 * `getRestaurantPillLabelProps` — the label props a member-facing restaurant
 * `TasteMatchPill` needs, so every surface reads the no-personal-TasteMatch
 * state the same way.
 *
 * The pill's default shape is "TasteMatch™" collapsed and "{tier} TasteMatch™"
 * on hover. That works for a real tier ("High TasteMatch™") but not for the
 * no-data state: "Not enough data TasteMatch™" does not parse, and the
 * collapsed text would hide the state behind a bare "TasteMatch™". So when the
 * restaurant has no personal TasteMatch (`hasPersonalRating` false: rating
 * null, or a `public_consensus`/`embedding`/`none` score source even if a
 * `rating` number leaked through) the label itself is "Unsure TasteMatch",
 * shown collapsed and on hover (the pill adds the ™, like "High TasteMatch™").
 * Same shape the group "Still Learning" fallback uses (`matchLabel ===
 * tierLabel`), which the pill already de-duplicates.
 *
 * A public/Google rating never changes this (CD, 2026-10-04).
 */
import { getCompatibilityEmoji, hasPersonalRating, type RatableRestaurant } from '@/utils/restaurantUtils';
import { tasteMatchBadgeCopy } from '@/copy/tasteMatchBadge';

export interface RestaurantPillLabelProps {
  tierLabel: string;
  matchLabel?: string;
}

/**
 * @param personalTierLabel Tier word to show for a real personal TasteMatch.
 *   Defaults to `getCompatibilityEmoji(restaurant).label`; the search card
 *   passes the `getTasteMatchBand` word instead. Ignored in the no-data state.
 */
export function getRestaurantPillLabelProps(
  restaurant: RatableRestaurant,
  personalTierLabel?: string,
): RestaurantPillLabelProps {
  if (!hasPersonalRating(restaurant)) {
    const label = tasteMatchBadgeCopy.label;
    return { tierLabel: label, matchLabel: label };
  }
  return { tierLabel: personalTierLabel ?? getCompatibilityEmoji(restaurant).label };
}
